import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CircularProgress from '@mui/material/CircularProgress';
import dayjs from 'dayjs';

const MAX_CSV_SIZE_BYTES = 5 * 1024 * 1024;

const getTodayDate = () => dayjs().format('YYYY-MM-DD');

const formatFileSize = (bytes) => {
  if (!bytes) {
    return '0 MB';
  }

  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const UploadAttendanceDialog = ({ open, onClose, onSubmit, shifts = [], isLoadingShifts = false }) => {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [selectedShiftId, setSelectedShiftId] = useState('');
  const [shiftError, setShiftError] = useState('');
  const [attendanceDate, setAttendanceDate] = useState(getTodayDate());
  const [markMissingAsAbsent, setMarkMissingAsAbsent] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setFile(null);
      setFileError('');
      setSelectedShiftId('');
      setShiftError('');
      setAttendanceDate(getTodayDate());
      setMarkMissingAsAbsent(true);
      setSubmitting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [open]);

  const handleClose = () => {
    if (!submitting) {
      onClose();
    }
  };

  const handleFileChange = (event) => {
    const uploadedFile = event.target.files?.[0] || null;

    if (!uploadedFile) {
      setFile(null);
      setFileError('');
      return;
    }

    if (!uploadedFile.name.toLowerCase().endsWith('.csv')) {
      setFile(null);
      setFileError('Invalid file type. Only CSV files are allowed.');
      return;
    }

    if (uploadedFile.size > MAX_CSV_SIZE_BYTES) {
      setFile(null);
      setFileError('File size exceeds 5 MB.');
      return;
    }

    setFile(uploadedFile);
    setFileError('');
  };

  const handleChooseFile = () => {
    fileInputRef.current?.click();
  };

  const handleClearFile = () => {
    setFile(null);
    setFileError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    if (!selectedShiftId) {
      setShiftError('Shift is required.');
      return;
    }

    if (!file) {
      setFileError('CSV file is required.');
      return;
    }

    if (fileError) {
      return;
    }

    setSubmitting(true);
    try {
      const success = await onSubmit({
        file,
        shiftId: selectedShiftId,
        attendanceDate,
        markMissingAsAbsent,
      });
      if (success) {
        onClose();
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth disableEscapeKeyDown={submitting}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <UploadFileIcon color="primary" />
        Upload Attendance CSV
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Upload a CSV file containing Employee ID, First Punch, Last Punch, and Total Time. The selected date below is used for the full file.
        </Typography>

        <Box sx={{ display: 'grid', gap: 2, mb: 2 }}>
          <FormControl fullWidth error={!!shiftError} disabled={submitting || isLoadingShifts}>
            <InputLabel id="attendance-upload-shift-label">Shift</InputLabel>
            <Select
              labelId="attendance-upload-shift-label"
              value={selectedShiftId}
              label="Shift"
              onChange={(event) => {
                setSelectedShiftId(event.target.value);
                setShiftError('');
              }}
            >
              {shifts.map((shift) => (
                <MenuItem key={shift.id} value={String(shift.id)}>
                  {`${shift.name} (${(shift.shift_start_time || '').slice(0, 5)} - ${(shift.shift_end_time || '').slice(0, 5)})`}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {shiftError || (isLoadingShifts ? 'Loading shifts...' : 'Select the employee shift to match against this upload.')}
            </FormHelperText>
          </FormControl>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Attendance Date"
              value={attendanceDate ? dayjs(attendanceDate) : null}
              maxDate={dayjs()}
              onChange={(newValue) => {
                setAttendanceDate(newValue ? dayjs(newValue).format('YYYY-MM-DD') : getTodayDate());
              }}
              disabled={submitting}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                  InputLabelProps: { shrink: true },
                },
              }}
            />
          </LocalizationProvider>

          <FormControlLabel
            control={(
              <Checkbox
                checked={markMissingAsAbsent}
                onChange={(event) => setMarkMissingAsAbsent(event.target.checked)}
                disabled={submitting}
              />
            )}
            label="Mark missing records as absent"
          />
          <FormHelperText sx={{ mt: -1 }}>
            When unchecked, employees in the selected shift without a valid CSV match will be left unchanged for the selected date.
          </FormHelperText>
        </Box>

        <Box
          sx={{
            border: '1px dashed',
            borderColor: fileError ? 'error.main' : 'divider',
            borderRadius: 1.5,
            p: 2,
            bgcolor: 'background.neutral',
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            hidden
            onChange={handleFileChange}
          />

          <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
            <Box display="flex" alignItems="center" gap={1.5}>
              <DescriptionOutlinedIcon color={file ? 'primary' : 'action'} />
              <Box>
                <Typography variant="subtitle2">
                  {file ? file.name : 'No file selected'}
                </Typography>
                <Typography variant="caption" color={fileError ? 'error.main' : 'text.secondary'}>
                  {fileError || `CSV only, max ${formatFileSize(MAX_CSV_SIZE_BYTES)}`}
                </Typography>
              </Box>
            </Box>

            <Box display="flex" gap={1}>
              {!file && (
                <Button variant="outlined" onClick={handleChooseFile} disabled={submitting}>
                  Choose File
                </Button>
              )}
              {file && (
                <Button onClick={handleClearFile} disabled={submitting}>
                  Clear
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting || !file || !!fileError}
          startIcon={submitting ? <CircularProgress size={20} /> : <UploadFileIcon />}
        >
          {submitting ? 'Uploading...' : 'Upload CSV'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

UploadAttendanceDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  shifts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      name: PropTypes.string,
      shift_start_time: PropTypes.string,
      shift_end_time: PropTypes.string,
    })
  ),
  isLoadingShifts: PropTypes.bool,
};

export default UploadAttendanceDialog;